import React, { useState } from 'react';
import { Users, Globe, Landmark, Star, CheckCircle } from 'lucide-react';
import PageLayout, { PageHero, PageSection, PageCTA } from '../components/common/PageLayout';
import SEOHead from '../components/common/SEOHead';
import { useLanguage } from '../i18n/LanguageContext';

const GALLERY = [
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/xy9crazs_IMG_0678.jpeg', title: 'Festival Masivo' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/y6j3ne04_IMG_0673.webp', title: 'Dubai Eye' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/5i2wfq6j_IMG_0675.jpeg', title: 'Tokyo Olympics' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/4apwl3ki_21CF7529-AF01-46E8-B5CB-37ADEE828E43.png', title: 'Año Nuevo' },
];

const benefitIcons = [Users, Globe, Landmark, Star];

const EventosMasivosPage = () => {
  const [lightboxImage, setLightboxImage] = useState(null);
  const { language } = useLanguage();

  const content = {
    es: {
      title: "Eventos Masivos,",
      titleHighlight: "Turismo y Gobierno",
      subtitle: "Espectáculos que transforman destinos y celebraciones públicas en experiencias inolvidables.",
      description: "Festivales, celebraciones nacionales, atracciones turísticas y eventos gubernamentales con impacto global.",
      whyChoose: "Experiencias para",
      whyChooseHighlight: "Gran Escala",
      benefits: [
        { title: "Alcance Masivo", desc: "Miles de espectadores presenciales y millones en redes sociales y medios." },
        { title: "Posicionamiento Turístico", desc: "Convierte tu destino en un lugar de visita obligada." },
        { title: "Identidad Cultural", desc: "Celebra la identidad de ciudades, estados y países con símbolos en el cielo." },
        { title: "Impacto Mediático", desc: "Cobertura nacional e internacional garantizada." },
      ],
      applications: "Aplicaciones",
      applicationsHighlight: "Perfectas",
      appList: [
        "Festivales y conciertos masivos",
        "Celebraciones de Año Nuevo e Independencia",
        "Inauguraciones de infraestructura pública",
        "Atracciones turísticas permanentes",
        "Eventos deportivos y olímpicos",
        "Promoción de destinos turísticos",
      ],
      gallery: "Eventos",
      galleryHighlight: "Realizados",
      ctaTitle: "¿Planeas un evento",
      ctaTitleHighlight: "de gran escala",
      ctaDescription: "Contáctanos para diseñar un espectáculo que impacte a miles de personas.",
    },
    en: {
      title: "Massive Events,",
      titleHighlight: "Tourism & Government",
      subtitle: "Spectacles that transform destinations and public celebrations into unforgettable experiences.",
      description: "Festivals, national celebrations, tourist attractions and government events with global impact.",
      whyChoose: "Experiences for",
      whyChooseHighlight: "Large Scale",
      benefits: [
        { title: "Massive Reach", desc: "Thousands of live spectators and millions on social media and press." },
        { title: "Tourism Positioning", desc: "Turn your destination into a must-visit place." },
        { title: "Cultural Identity", desc: "Celebrate the identity of cities, states and countries with symbols in the sky." },
        { title: "Media Impact", desc: "National and international coverage guaranteed." },
      ],
      applications: "Perfect",
      applicationsHighlight: "Applications",
      appList: [
        "Massive festivals and concerts",
        "New Year and Independence celebrations",
        "Public infrastructure inaugurations",
        "Permanent tourist attractions",
        "Sports and Olympic events",
        "Tourism destination promotion",
      ],
      gallery: "Completed",
      galleryHighlight: "Events",
      ctaTitle: "Planning a",
      ctaTitleHighlight: "large-scale event",
      ctaDescription: "Contact us to design a spectacle that impacts thousands of people.",
    }
  };

  const c = content[language] || content.es;

  return (
    <PageLayout>
      <SEOHead 
        title={language === 'es' ? "Eventos Masivos con Drones | Turismo y Gobierno | NexAir" : "Massive Events with Drones | Tourism & Government | NexAir"}
        description={language === 'es' ? "Shows de drones para eventos masivos, celebraciones públicas, turismo y gobierno. Espectáculos de gran escala que impactan a miles." : "Drone shows for massive events, public celebrations, tourism and government. Large-scale spectacles that impact thousands."}
        keywords={language === 'es' ? "eventos masivos drones, show drones turismo, drones gobierno, celebraciones publicas" : "massive events drones, drone show tourism, government drones, public celebrations"}
        canonicalUrl="https://nexair.com.mx/eventos-masivos"
      />
      
      <PageHero 
        image="https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/xy9crazs_IMG_0678.jpeg"
        title={c.title}
        titleHighlight={c.titleHighlight}
        subtitle={c.subtitle}
        description={c.description}
      />
      
      {/* Benefits */}
      <PageSection className="bg-[#030810]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center font-heading">
          {c.whyChoose} <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">{c.whyChooseHighlight}</span>
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {c.benefits.map((benefit, index) => {
            const Icon = benefitIcons[index];
            return (
              <div key={index} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-cyan-500/50 transition-all">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-cyan-500/20 to-blue-500/20 flex items-center justify-center mb-4">
                  <Icon className="w-7 h-7 text-cyan-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 font-heading">{benefit.title}</h3>
                <p className="text-gray-400">{benefit.desc}</p>
              </div>
            );
          })}
        </div>
      </PageSection>

      {/* Applications */}
      <PageSection className="bg-[#050a15]">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 font-heading">
              {c.applications} <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">{c.applicationsHighlight}</span>
            </h2>
            <ul className="space-y-4">
              {c.appList.map((item, index) => (
                <li key={index} className="flex items-center gap-4 text-lg text-gray-300">
                  <CheckCircle className="w-6 h-6 text-cyan-400 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <img 
              src="https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/4apwl3ki_21CF7529-AF01-46E8-B5CB-37ADEE828E43.png"
              alt="Massive Event"
              className="rounded-2xl shadow-2xl"
            />
          </div>
        </div>
      </PageSection>
      
      {/* Gallery */}
      <PageSection className="bg-[#030810]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center font-heading">
          {c.gallery} <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">{c.galleryHighlight}</span>
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {GALLERY.map((img, index) => (
            <div 
              key={index}
              className="relative aspect-square rounded-xl overflow-hidden cursor-pointer group"
              onClick={() => setLightboxImage(img.url)}
            >
              <img 
                src={img.url}
                alt={img.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <span className="text-white font-medium">{img.title}</span>
              </div>
            </div>
          ))}
        </div>
      </PageSection>
      
      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <img src={lightboxImage} alt="Gallery" className="max-w-full max-h-[90vh] object-contain rounded-lg" />
          <button className="absolute top-6 right-6 text-white/70 hover:text-white text-4xl" onClick={() => setLightboxImage(null)}>×</button>
        </div>
      )}
      
      <PageCTA 
        title={c.ctaTitle}
        titleHighlight={c.ctaTitleHighlight}
        description={c.ctaDescription}
      />
    </PageLayout>
  );
};

export default EventosMasivosPage;
